import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { Chip } from "components/AtomsV2";
import { Icon } from "components/ShellV2";

const MODULE_ICONS = {
  OSTEC: "battery_charging_full",
  OSTEC_PROMOTOR: "event_available",
  GDBN: "bolt",
  EV: "ev_station",
  EWH: "water_heater",
  HP: "heat_pump",
  EXTERNAL: "cloud_sync",
};

const MODULE_TINTS = {
  OSTEC: "rec-tint-teal",
  OSTEC_PROMOTOR: "rec-tint-info",
  GDBN: "rec-tint-amber",
  EV: "rec-tint-info",
  EWH: "rec-tint-green",
  HP: "rec-tint-info",
  EXTERNAL: "rec-tint-teal",
};

function moduleVisual(moduleType) {
  const key = Object.keys(MODULE_ICONS).find((k) => moduleType?.toUpperCase().startsWith(k));
  return {
    icon: key ? MODULE_ICONS[key] : "widgets",
    tint: key ? MODULE_TINTS[key] : "rec-tint-teal",
  };
}

const STATUS_COLOR = {
  SUCCESS: "var(--success)",
  FAILED: "var(--danger)",
  RUNNING: "var(--warning)",
};

function ModuleCard({
  module,
  isActive,
  config,
  lastRunLabel,
  isCalculating,
  onActivate,
  onConfigure,
  onCalculate,
  onDeactivate,
}) {
  const { t } = useTranslation(["community"]);
  const { icon, tint } = moduleVisual(module.module_type);
  const statusColor = config?.last_run_status ? STATUS_COLOR[config.last_run_status] : null;

  return (
    <div className={`rec-ems-card${isActive ? " active" : ""}`}>
      <div className="rec-ems-card-head">
        <div className={`rec-ems-ic ${tint}`}>
          <Icon name={icon} fill />
        </div>
        <div className="flex-1 min-w-0">
          <b>{module.display_name}</b>
          {(isActive || module.requires_external_api) && (
            <div className="flex gap-[6px] mt-[5px] flex-wrap items-center">
              {isActive && (
                <Chip tone="green" className="!text-[10px] !h-5">
                  {t("unified_ems.active")}
                </Chip>
              )}
              {module.requires_external_api && (
                <Chip tone="info" className="!text-[10px] !h-5">
                  <Icon name="cloud" style={{ fontSize: 12 }} />
                  {t("unified_ems.external_api")}
                </Chip>
              )}
              {config?.last_run_status === "RUNNING" ? (
                <span
                  className="animate-spin inline-flex shrink-0"
                  title={t("unified_ems.calculating")}
                >
                  <Icon
                    name="progress_activity"
                    style={{ fontSize: 13, color: "var(--warning)" }}
                  />
                </span>
              ) : (
                statusColor && (
                  <span
                    className="inline-block w-[7px] h-[7px] rounded-full shrink-0"
                    style={{ background: statusColor }}
                    title={config.last_run_status}
                  />
                )
              )}
            </div>
          )}
        </div>
      </div>
      <p className="rec-ems-desc">{module.description}</p>
      <div className="rec-ems-caps">
        {module.capabilities.map((capability) => (
          <span key={capability} className="rec-ems-cap">
            {capability}
          </span>
        ))}
      </div>
      <div className="rec-ems-foot">
        <span className="ver">
          {`v${module.version}`}
          {isActive && lastRunLabel ? ` · ${lastRunLabel}` : ""}
        </span>
        <div className="flex-1" />
        {isActive ? (
          <>
            <button
              type="button"
              className="rec-btn-ghost rec-btn-sm"
              disabled={isCalculating}
              onClick={onCalculate}
            >
              {isCalculating ? (
                <span className="animate-spin inline-flex">
                  <Icon name="progress_activity" style={{ fontSize: 16 }} />
                </span>
              ) : (
                <Icon name="play_arrow" />
              )}
              {t("unified_ems.calculate")}
            </button>
            <button
              type="button"
              className="rec-icon-btn !w-8 !h-8"
              title={t("unified_ems.configure")}
              onClick={onConfigure}
            >
              <Icon name="settings" style={{ fontSize: 18 }} />
            </button>
            <button
              type="button"
              className="rec-icon-btn !w-8 !h-8"
              title={t("unified_ems.deactivate")}
              onClick={onDeactivate}
            >
              <Icon name="power_settings_new" style={{ fontSize: 18 }} />
            </button>
          </>
        ) : (
          <button type="button" className="rec-btn-dark rec-btn-sm" onClick={onActivate}>
            <Icon name="play_circle" />
            {t("unified_ems.activate")}
          </button>
        )}
      </div>
    </div>
  );
}

ModuleCard.propTypes = {
  module: PropTypes.shape({
    module_type: PropTypes.string.isRequired,
    display_name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    version: PropTypes.string.isRequired,
    capabilities: PropTypes.arrayOf(PropTypes.string).isRequired,
    requires_external_api: PropTypes.bool,
  }).isRequired,
  isActive: PropTypes.bool,
  config: PropTypes.shape({
    last_run_status: PropTypes.string,
  }),
  lastRunLabel: PropTypes.string,
  isCalculating: PropTypes.bool,
  onActivate: PropTypes.func,
  onConfigure: PropTypes.func,
  onCalculate: PropTypes.func,
  onDeactivate: PropTypes.func,
};

ModuleCard.defaultProps = {
  isActive: false,
  config: null,
  lastRunLabel: null,
  isCalculating: false,
  onActivate: undefined,
  onConfigure: undefined,
  onCalculate: undefined,
  onDeactivate: undefined,
};

export default ModuleCard;
